import { Note, noteSchema } from 'app/agriculture/queries/notes/types/Note';
import { Task, taskSchema } from 'app/agriculture/queries/tasks/types/Task';
import { Pineapple, pineappleSchema } from 'app/agriculture/jest/utils/pineappleSchema';
import { Device, deviceSchema } from 'app/agriculture/queries/devices/types/Device';
import { Herd, herdSchema } from 'app/agriculture/queries/herds/types/Herd';
import { TaskCategory, taskCategorySchema } from 'app/agriculture/queries/tasks/types/TaskCategory';
import {
    ProtagAnimal,
    protagAnimalSchema,
} from 'app/agriculture/queries/protagAnimals/types/ProtagAnimal';
import { Tank, tankSchema } from 'app/agriculture/queries/tanks/types/Tank';
import { z } from 'zod';
import { ApiEndpoint } from 'app/agriculture/queries/apiEndpoints';
import {
    Paddock,
    paddockSchema,
} from 'app/agriculture/queries/entities/types/infrastructure/Paddock';
import {
    GasbotDevice,
    gasbotDeviceSchema,
} from 'app/agriculture/queries/devices/types/GasbotDevice';
import {
    BridgeCrossing,
    bridgeCrossingSchema,
} from 'app/agriculture/queries/entities/types/infrastructure/BridgeCrossing';
import {
    CoolRoom,
    coolRoomSchema,
} from 'app/agriculture/queries/entities/types/infrastructure/CoolRoom';
import { Gate, gateSchema } from 'app/agriculture/queries/entities/types/infrastructure/Gate';
import { House, houseSchema } from 'app/agriculture/queries/entities/types/infrastructure/House';
import { Silo, siloSchema } from 'app/agriculture/queries/entities/types/infrastructure/Silo';
import { Shed, shedSchema } from 'app/agriculture/queries/entities/types/infrastructure/Shed';
import { Entity } from 'app/agriculture/queries/entities/types/Entity';
import { EntityType } from 'app/agriculture/models/Entities';
import {
    ErosionArea,
    erosionAreaSchema,
} from 'app/agriculture/queries/entities/types/infrastructure/ErosionArea';
import {
    Boundary,
    boundarySchema,
} from 'app/agriculture/queries/entities/types/infrastructure/Boundary';
import {
    Vegetation,
    vegetationSchema,
} from 'app/agriculture/queries/entities/types/infrastructure/Vegetation';
import { Yard, yardSchema } from 'app/agriculture/queries/entities/types/infrastructure/Yard';
import { Dam, damSchema } from 'app/agriculture/queries/entities/types/infrastructure/Dam';
import { Fence, fenceSchema } from 'app/agriculture/queries/entities/types/infrastructure/Fence';
import {
    Waterway,
    waterwaySchema,
} from 'app/agriculture/queries/entities/types/infrastructure/Waterway';
import { Trough, troughSchema } from '../../queries/entities/types/infrastructure/Trough';

export interface entityConfigToType {
    Boundary: Boundary;
    'Bridge Crossing': BridgeCrossing;
    'Cool Room': CoolRoom;
    Dam: Dam;
    Device: Device;
    'Erosion Area': ErosionArea;
    Fence: Fence;
    GasbotDevice: GasbotDevice;
    Gate: Gate;
    Herd: Herd;
    House: House;
    Trough: Trough;
    Note: Note;
    Paddock: Paddock;
    ProtagAnimal: ProtagAnimal;
    Shed: Shed;
    Silo: Silo;
    Tank: Tank;
    Task: Task;
    TaskCategory: TaskCategory;
    Vegetation: Vegetation;
    Waterway: Waterway;
    Yard: Yard;

    // for testing
    Test: Pineapple;
}

export const InfraTypes = [
    'Boundary',
    'Bridge Crossing',
    'Cool Room',
    'Dam',
    'Erosion Area',
    'Fence',
    'Gate',
    'House',
    'Trough',
    'Paddock',
    'Shed',
    'Silo',
    'Tank',
    'Vegetation',
    'Waterway',
    'Yard',
] as const;

export type InfraType = (typeof InfraTypes)[number];

export const MutableEntityTypeDef = [
    'Note',
    'Task',
    'Device',
    'GasbotDevice',
    'Herd',
    'ProtagAnimal',
    'TaskCategory',
    ...InfraTypes,
] as const;

export const allMutableEntityTypes = [...MutableEntityTypeDef];

// This might just be the same as EntityType in the future
export type MutableEntityType = (typeof MutableEntityTypeDef)[number] | 'Test';

export function isMutableEntityType(x: string): x is MutableEntityType {
    return allMutableEntityTypes.some((type) => x === type);
}

interface EntityConfig {
    endpoint: string;
    schema: z.ZodSchema;
    genericEntityTransform: z.ZodSchema;
}

export function isInfraType(x: EntityType | MutableEntityType): x is InfraType {
    return InfraTypes.some((infraType) => infraType === x);
}

export const entityConfig = {
    Boundary: {
        endpoint: ApiEndpoint.boundaries,
        schema: boundarySchema,
        genericEntityTransform: boundarySchema.transform<Entity>(({ id, creatorId, name }) => ({
            type: 'Boundary',
            id,
            name,
            creatorId,
        })),
    },
    'Bridge Crossing': {
        endpoint: ApiEndpoint.bridgeCrossings,
        schema: bridgeCrossingSchema,
        genericEntityTransform: bridgeCrossingSchema.transform<Entity>(
            ({ id, creatorId, name, geometry: { type, coordinates } }) => ({
                type: 'Bridge Crossing',
                id,
                name,
                creatorId,
                geometry: { type, coordinates: [coordinates[0], coordinates[1]] },
            }),
        ),
    },
    'Cool Room': {
        endpoint: ApiEndpoint.coolRooms,
        schema: coolRoomSchema,
        genericEntityTransform: coolRoomSchema.transform<Entity>(
            ({ id, creatorId, name, geometry: { type, coordinates } }) => ({
                type: 'Cool Room',
                id,
                name,
                creatorId,
                geometry: { type, coordinates: [coordinates[0], coordinates[1]] },
            }),
        ),
    },
    Dam: {
        endpoint: ApiEndpoint.dams,
        schema: damSchema,
        genericEntityTransform: damSchema.transform<Entity>(({ id, name }) => ({
            type: 'Dam',
            id,
            name,
        })),
    },
    Device: {
        endpoint: ApiEndpoint.devices,
        schema: deviceSchema,
        genericEntityTransform: deviceSchema.transform<Entity>(({ id, name }) => ({
            type: 'Device',
            id,
            name,
        })),
    },
    'Erosion Area': {
        endpoint: ApiEndpoint.erosionAreas,
        schema: erosionAreaSchema,
        genericEntityTransform: erosionAreaSchema.transform<Entity>(({ id, creatorId, name }) => ({
            type: 'Erosion Area',
            id,
            name,
            creatorId,
        })),
    },
    Fence: {
        endpoint: ApiEndpoint.fences,
        schema: fenceSchema,
        genericEntityTransform: fenceSchema.transform<Entity>(({ id, creatorId, name }) => ({
            type: 'Fence',
            id,
            name,
            creatorId,
        })),
    },
    GasbotDevice: {
        endpoint: ApiEndpoint.gasbotDevices,
        schema: gasbotDeviceSchema,
        genericEntityTransform: gasbotDeviceSchema.transform<Entity>(({ id, name }) => ({
            type: 'Device',
            id,
            name,
        })),
    },
    Gate: {
        endpoint: ApiEndpoint.gates,
        schema: gateSchema,
        genericEntityTransform: gateSchema.transform<Entity>(
            ({ id, creatorId, name, geometry: { type, coordinates } }) => ({
                type: 'Gate',
                id,
                name,
                creatorId,
                geometry: { type, coordinates: [coordinates[0], coordinates[1]] },
            }),
        ),
    },
    Herd: {
        endpoint: ApiEndpoint.herds,
        schema: herdSchema,
        genericEntityTransform: herdSchema.transform<Entity>(({ id, name, creatorId }) => ({
            type: 'Herd',
            id,
            name,
            creatorId,
        })),
    },
    Trough: {
        endpoint: ApiEndpoint.troughs,
        schema: troughSchema,
        genericEntityTransform: troughSchema.transform<Entity>(
            ({ id, creatorId, name, geometry: { type, coordinates } }) => ({
                type: 'Trough',
                id,
                name,
                creatorId,
                geometry: { type, coordinates: [coordinates[0], coordinates[1]] },
            }),
        ),
    },
    House: {
        endpoint: ApiEndpoint.houses,
        schema: houseSchema,
        genericEntityTransform: houseSchema.transform<Entity>(
            ({ id, creatorId, name, geometry: { type, coordinates } }) => ({
                type: 'House',
                id,
                name,
                creatorId,
                geometry: { type, coordinates: [coordinates[0], coordinates[1]] },
            }),
        ),
    },
    Note: {
        endpoint: ApiEndpoint.notes,
        schema: noteSchema,
        genericEntityTransform: noteSchema.transform<Entity>(({ id, creatorId, location }) => ({
            type: 'Note',
            id,
            creatorId,
            geometry:
                location == null
                    ? undefined
                    : {
                          type: 'Point',
                          coordinates: [location.longitude, location.latitude],
                      },
        })),
    },
    Paddock: {
        endpoint: ApiEndpoint.paddocks,
        schema: paddockSchema,
        genericEntityTransform: paddockSchema.transform<Entity>(({ id, creatorId, name }) => ({
            type: 'Paddock',
            id,
            name,
            creatorId,
        })),
    },
    ProtagAnimal: {
        endpoint: ApiEndpoint.protagAnimals,
        schema: protagAnimalSchema,
        genericEntityTransform: protagAnimalSchema.transform<Entity>(({ id, name }) => ({
            type: 'ProtagAnimal',
            id,
            name,
        })),
    },
    Shed: {
        endpoint: ApiEndpoint.sheds,
        schema: shedSchema,
        genericEntityTransform: shedSchema.transform<Entity>(
            ({ id, creatorId, name, geometry: { type, coordinates } }) => ({
                type: 'Shed',
                id,
                name,
                creatorId,
                geometry: { type, coordinates: [coordinates[0], coordinates[1]] },
            }),
        ),
    },
    Silo: {
        endpoint: ApiEndpoint.silos,
        schema: siloSchema,
        genericEntityTransform: siloSchema.transform<Entity>(
            ({ id, creatorId, name, geometry: { type, coordinates } }) => ({
                type: 'Silo',
                id,
                name,
                creatorId,
                geometry: { type, coordinates: [coordinates[0], coordinates[1]] },
            }),
        ),
    },
    Tank: {
        endpoint: ApiEndpoint.tanks,
        schema: tankSchema,
        genericEntityTransform: tankSchema.transform<Entity>(
            ({ id, creatorId, name, geometry: { type, coordinates } }) => ({
                type: 'Tank',
                id,
                name,
                geometry: { type, coordinates: [coordinates[0], coordinates[1]] },
                creatorId,
            }),
        ),
    },
    Task: {
        endpoint: ApiEndpoint.tasks,
        schema: taskSchema,
        genericEntityTransform: taskSchema.transform<Entity>(
            ({ id, creatorId, title, location }) => ({
                type: 'Task',
                id,
                name: title,
                creatorId,
                geometry:
                    location == null
                        ? undefined
                        : {
                              type: 'Point',
                              coordinates: [location.longitude, location.latitude],
                          },
            }),
        ),
    },
    TaskCategory: {
        endpoint: ApiEndpoint.taskCategories,
        schema: taskCategorySchema,
        genericEntityTransform: taskCategorySchema.transform<Entity>(({ id, name }) => ({
            type: 'TaskCategory',
            id,
            name,
        })),
    },
    Vegetation: {
        endpoint: ApiEndpoint.vegetation,
        schema: vegetationSchema,
        genericEntityTransform: vegetationSchema.transform<Entity>(({ id, creatorId, name }) => ({
            type: 'Vegetation',
            id,
            name,
            creatorId,
        })),
    },
    Waterway: {
        endpoint: ApiEndpoint.waterways,
        schema: waterwaySchema,
        genericEntityTransform: waterwaySchema.transform<Entity>(({ id, creatorId, name }) => ({
            type: 'Waterway',
            id,
            name,
            creatorId,
        })),
    },
    Yard: {
        endpoint: ApiEndpoint.yards,
        schema: yardSchema,
        genericEntityTransform: yardSchema.transform<Entity>(({ id, creatorId, name }) => ({
            type: 'Yard',
            id,
            name,
            creatorId,
        })),
    },

    // for testing
    Test: {
        endpoint: ApiEndpoint.test,
        schema: pineappleSchema,
        genericEntityTransform: pineappleSchema.transform<Entity>(({ id, name }) => ({
            type: 'Test',
            id,
            name,
        })),
    },
} as const satisfies Record<MutableEntityType, EntityConfig>;
